function SpecRow({ label, value }) {
  return (
    <>
      <span className="font-bold text-gray-900 text-sm whitespace-nowrap leading-snug">
        {label}:
      </span>
      <span className="text-gray-700 text-sm leading-snug">{value || "-"}</span>
    </>
  );
}

function cleanText(value) {
  return String(value ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function withUnit(value, unit) {
  if (value === undefined || value === null || value === "") return "-";
  const text = String(value).trim();
  if (!text) return "-";
  return text.toLowerCase().includes(unit.toLowerCase()) ? text : `${text} ${unit}`;
}

function termById(terms = [], id) {
  const numericId = Number(id);
  return terms.find((term) => Number(term.id) === numericId) ?? null;
}

function modalData(info) {
  if (info?.dynamic) {
    const config = info.productConfig || {};
    const product = config.product || {};
    const properties = config.properties || {};
    const measurements = config.measurements || {};
    const shape = termById(config.shapes, properties.shape_id);
    const size =
      properties.total_wardrobe_size ||
      measurements.total_size ||
      properties.wardrobe_size ||
      info.design?.size;
    const title = `${shape?.name || "Wardrobe"} Wardrobe`.toUpperCase();

    return {
      title,
      subtitle: product.name || `${withUnit(size, "LM")} ${shape?.name || "Wardrobe"}`,
      articleNo: product.article_number || info.design?.id || product.id || "-",
      woodMaterial: properties.wood_material || "Laminate Bonded Chipboards",
      glassFrame: properties.glass_frame || "Aluminum Frame",
      drawerRail: properties.drawer_rail || "Hafele - Soft Closing",
      hinges: properties.hinges || "Bergo - Soft Closing",
      handleType: properties.handle_type || "Integrated Handle",
      totalSize: withUnit(size, "LM"),
      carcassDepth: withUnit(properties.carcass_depth || measurements.carcass_depth || "600", "mm"),
      description:
        cleanText(product.description) ||
        "Wardrobe accessories and/or clothes are displayed for illustration purposes. It is excluded from the price. Price includes delivery and installation.",
    };
  }

  const specs = info?.specs || {};
  return {
    title: `${info?.label || "Wardrobe"} Wardrobe`.toUpperCase(),
    subtitle: specs.productName || `${withUnit(info?.size, "LM")} Wardrobe`,
    articleNo: info?.id || "-",
    woodMaterial: specs.woodMaterial || "Laminate Bonded Chipboards",
    glassFrame: specs.glassFrame || "Aluminum Frame",
    drawerRail: specs.drawerRails || "Hafele - Soft Closing",
    hinges: specs.hinges || "Bergo - Soft Closing",
    handleType: specs.handleType || "Integrated Handle",
    totalSize: withUnit(info?.size, "LM"),
    carcassDepth: specs.carcassDepth || "600 mm",
    description:
      "Wardrobe accessories and/or clothes are displayed for illustration purposes. It is excluded from the price. Price includes delivery and installation.",
  };
}

export default function InformationModal({ info, onClose }) {
  if (!info) return null;

  const data = modalData(info);

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 md:p-6"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-4xl md:rounded-[40px] max-w-3xl w-full p-6 md:p-10 overflow-y-auto max-h-[90vh]"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          {/* Title + subtitle */}
          <div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-gray-900 leading-none tracking-tight uppercase">
              {data.title}
            </h2>
            <p className="text-sm font-semibold text-gray-800 mt-2">
              {data.subtitle}
            </p>
          </div>

          {/* Article No */}
          <div className="sm:ml-8 sm:shrink-0">
            <p className="text-xs text-gray-400 tracking-widest uppercase mb-1.5">
              Article No
            </p>
            <div className="bg-gray-600 text-white font-bold text-base px-6 py-2 text-center min-w-[16ch]">
              {data.articleNo}
            </div>
          </div>
        </div>

        {/* Divider */}
        <hr className="border-gray-300 mb-5" />

        {/* Specs
             Mobile:  one shared grid — all rows share the same label column width
             Desktop: two side-by-side grids via flex */}
        <div className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-0.5 sm:flex sm:gap-16 mb-6 md:mb-8">
          {/* Group 1 + 2 */}
          <div className="contents sm:grid sm:grid-cols-[max-content_1fr] sm:gap-x-4 sm:gap-y-0.5 sm:content-start sm:self-start">
            <SpecRow label="Wood Material" value={data.woodMaterial} />
            <SpecRow label="Glass Frame" value={data.glassFrame} />
            <div className="col-span-2 h-4" />
            <SpecRow label="Total Wardrobe Size" value={data.totalSize} />
            <SpecRow label="Carcass Depth" value={data.carcassDepth} />
          </div>

          {/* Group 3 */}
          <div className="contents sm:grid sm:grid-cols-[max-content_1fr] sm:gap-x-4 sm:gap-y-0.5 sm:content-start sm:self-start">
            <div className="col-span-2 h-4 sm:hidden" />
            <SpecRow label="Drawer Rail" value={data.drawerRail} />
            <SpecRow label="Hinges" value={data.hinges} />
            <SpecRow label="Handle Type" value={data.handleType} />
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-xs md:text-sm text-gray-500 leading-relaxed">
          {data.description}
        </p>
      </div>
    </div>
  );
}
